import { formatPropertyAddress } from '../utils/propertyAddress';
import { propertyFetch } from '../services/idxPropertyClient';
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import './IDXTownPage.css';
import SEOLinks from './SEOLinks';

const WestminsterTownhomesPage = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [propertyImages, setPropertyImages] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.classList.remove('intro-active');
    document.body.style.overflow = 'auto';
    document.body.style.position = 'static';
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
    };
  }, [isDarkMode]);

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary?city=Westminster&propertySubType=Townhouse&firstPhoto=true&hasMedia=true&limit=9`, {});
        if (response.ok) {
          const data = await response.json();
          const props = data.properties || [];
          setProperties(props);
          props.forEach(p => {
            if (p.listing_key) {
              propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-media/${p.listing_key}`, {}).then(r => r.json()).then(d => {
                const first = (d.media || []).sort((a, b) => parseInt(a.order_hint) - parseInt(b.order_hint))[0];
                const url = first?.media_url || first?.s3_link;
                if (url) setPropertyImages(prev => ({ ...prev, [p.listing_key]: url }));
              }).catch(() => {});
            }
          });
        }
      } catch (error) {
        console.error('Error fetching Westminster townhomes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperties();
  }, []);

  return (
    <div className="idxtown-page">
      <Helmet>
        <title>Westminster Townhomes for Sale | Modern Living | Truc Doan</title>
        <meta name="description" content="Browse Westminster townhomes for sale. Enjoy modern living with the perfect balance of space and low maintenance. Newer communities, garage parking, and shared amenities. Contact Truc Doan today." />
        <meta name="keywords" content="Westminster townhomes for sale, Westminster CA townhouse, townhome living Westminster, modern townhomes Orange County, Westminster attached homes, garage parking townhome Westminster, shared amenities Westminster, Truc Doan Westminster townhomes" />
        <link rel="canonical" href={`${SEO_CONFIG.DOMAIN}/westminster-townhomes`} />
        <meta property="og:title" content="Westminster Townhomes for Sale | Modern Living | Truc Doan" />
        <meta property="og:description" content="Find your perfect Westminster townhome. Modern communities with garage parking, shared amenities, and low-maintenance living." />
        <meta property="og:url" content={`${SEO_CONFIG.DOMAIN}/westminster-townhomes`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={SEO_CONFIG.OG_IMAGE} />
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="Westminster, California" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Truc Doan",
            "telephone": SEO_CONFIG.AGENT_PHONE,
            "email": SEO_CONFIG.AGENT_EMAIL,
            "url": `${SEO_CONFIG.DOMAIN}/westminster-townhomes`,
            "image": SEO_CONFIG.OG_IMAGE,
            "description": "Truc Doan specializes in Westminster townhomes for sale. Find modern townhouse living with garage parking, shared amenities, and newer communities.",
            "areaServed": {
              "@type": "City",
              "name": "Westminster, California"
            },
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Westminster",
              "addressRegion": "CA",
              "postalCode": "92683",
              "addressCountry": "US"
            }
          })}
        </script>
      </Helmet>

      <div className="idxtown-container">
        <button onClick={() => navigate('/')} style={{ position: 'absolute', top: '20px', left: '20px', padding: '10px 20px', background: 'rgb(14, 165, 233)', color: 'rgb(243, 244, 246)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', zIndex: 10 }}>
          {t('nav.home')}
        </button>

        <header className="idxtown-header">
          <h1>Westminster Townhomes for Sale</h1>
          <p style={{ marginTop: '16px', fontSize: '18px', opacity: 0.9 }}>
            Modern townhome living in Westminster, CA — the perfect balance of space, style, and convenience.
          </p>
        </header>

        <section className="idxtown-vibe">
          <h2>Why Choose Townhome Living in Westminster?</h2>
          <p>Westminster townhomes offer the ideal balance between single-family home space and low-maintenance condo living. With newer communities featuring modern floor plans, attached garages, and shared amenities, townhomes are one of the most popular housing choices for families, professionals, and downsizers in Westminster.</p>
          <p>Many Westminster townhome communities include private patios, community pools, playgrounds, and walking paths — all while keeping HOA fees reasonable and eliminating the hassle of exterior maintenance and landscaping.</p>
        </section>

        <section className="idxtown-lifestyle">
          <h2>Westminster Townhome Living Benefits</h2>
          <p>Discover what makes Westminster townhomes a smart choice for today's buyers.</p>
          <div className="amenities-list">
            <div className="amenity">
              <h3>Balance of Space & Maintenance</h3>
              <p>Enjoy multi-level living with 2-3 bedrooms and private outdoor space without the burden of maintaining a large yard or roof repairs.</p>
            </div>
            <div className="amenity">
              <h3>Newer Communities</h3>
              <p>Many Westminster townhomes were built in the last 10-20 years, featuring open floor plans, modern kitchens, and energy-efficient appliances.</p>
            </div>
            <div className="amenity">
              <h3>Garage Parking</h3>
              <p>Most townhomes include attached 2-car garages with direct interior access — a major advantage over condos and apartments in the area.</p>
            </div>
            <div className="amenity">
              <h3>Shared Amenities</h3>
              <p>Community pools, fitness centers, clubhouses, and landscaped common areas are standard in many Westminster townhome developments.</p>
            </div>
            <div className="amenity">
              <h3>Prime Location</h3>
              <p>Westminster townhomes are close to Little Saigon shopping, freeways, parks, and top-rated schools — perfect for commuters and families.</p>
            </div>
          </div>
        </section>

        <section className="market-snapshot">
          <h2>Westminster Townhome Market Snapshot</h2>
          <p>Here's what the current Westminster townhome market looks like for buyers and sellers.</p>
          <table className="market-stats">
            <thead>
              <tr>
                <th>Market Stat</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Median Townhome Price</td><td>~$650,000</td></tr>
              <tr><td>Average Days on Market</td><td>20–28 Days</td></tr>
              <tr><td>Most Popular Price Range</td><td>$500K – $800K</td></tr>
              <tr><td>Common Sizes</td><td>2–3 Bedrooms, 1,200–1,800 sq ft</td></tr>
              <tr><td>Typical HOA</td><td>$200 – $400/month</td></tr>
            </tbody>
          </table>
        </section>

        <section className="properties-section">
          <h2>Westminster Townhomes Currently for Sale</h2>
          {loading ? (
            <p style={{ color: 'rgb(156, 163, 175)' }}>{t('property.loadingProperties')}</p>
          ) : properties.length > 0 ? (
            <div className="properties-grid">
              {properties.slice(0, 9).map((property) => (
                <div key={property.listing_key} className="property-card" onClick={() => navigate(`/listing/${property.listing_key}`)} style={{ backgroundImage: (propertyImages[property.listing_key] || property.first_photo_url) ? `url(${propertyImages[property.listing_key] || property.first_photo_url})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                  <div className="property-price">${property.current_price?.toLocaleString()}</div>
                  <div className="property-address">{formatPropertyAddress(property)}, CA {property.postal_code}</div>
                  <div className="property-type">{property.property_sub_type}</div>
                  {(property.list_agent_full_name || property.list_office_name) && (
                    <div style={{fontSize: '11px', color: '#ddd', marginTop: '2px'}}>{t('landing.listedBy')}: {[property.list_agent_full_name, property.list_office_name].filter(Boolean).join(' | ')}</div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgb(156, 163, 175)' }}>No townhomes currently listed. Contact Truc Doan for off-market opportunities.</p>
          )}
        </section>

        <section className="cta-section">
          <h2>Ready to Find Your Westminster Townhome?</h2>
          <p>Let Truc Doan help you find the perfect townhome in Westminster. Whether you're a first-time buyer or looking to downsize, I'll guide you every step of the way.</p>
          <div className="cta-buttons">
            <a href={`mailto:${SEO_CONFIG.AGENT_EMAIL}?subject=Westminster Townhome Inquiry`} className="cta-button primary">{t('county.emailAgent')}</a>
            <a href={`tel:${SEO_CONFIG.AGENT_PHONE}`} className="cta-button secondary">{t('county.callAgent', { phone: SEO_CONFIG.AGENT_PHONE })}</a>
            <button className="cta-button primary" onClick={() => navigate('/chat')} style={{ border: 'none', cursor: 'pointer' }}>Search Townhomes</button>
          </div>
        </section>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.85)', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/crmlslogo.png" alt="CRMLS" style={{ height: '20px', objectFit: 'contain' }} />
        <img src="https://aw-client-logos-and-headshots.s3.us-east-2.amazonaws.com/logos/equalhousinglogo.png" alt="Equal Housing Opportunity" style={{ height: '20px', objectFit: 'contain' }} />
        <span style={{ fontSize: '10px', color: '#ccc', flex: 1 }}>Based on information from California Regional Multiple Listing Service, Inc. and/or other sources. All data, including all measurements and calculations of area, is obtained from various sources and has not been, and will not be, verified by broker or MLS. All information should be independently reviewed and verified for accuracy. Properties may or may not be listed by the office/agent presenting the information.</span>
      </div>
      <SEOLinks />
    </div>
  );
};

export default WestminsterTownhomesPage;
